import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  FolderKanban, Mail, Users,
  ArrowUpRight, ArrowDownRight, ExternalLink
} from 'lucide-react';
import { useApi } from '../AdminApp';

const StatCard = ({ title, value, change, changeType, icon: Icon, color, link }) => (
  <Link
    to={link}
    className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50 hover:border-gray-600 transition-all duration-300 group"
  >
    <div className="flex items-start justify-between">
      <div>
        <p className="text-gray-400 text-sm font-medium">{title}</p>
        <p className="text-3xl font-bold text-white mt-2">{value}</p>
        {change && (
          <div className={`flex items-center gap-1 mt-2 text-sm ${changeType === 'up' ? 'text-green-400' : 'text-red-400'}`}>
            {changeType === 'up' ? <ArrowUpRight className="w-4 h-4" /> : <ArrowDownRight className="w-4 h-4" />}
            <span>{change}</span>
          </div>
        )}
      </div>
      <div className={`p-3 rounded-xl ${color} group-hover:scale-110 transition-transform duration-300`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </Link>
);

const RecentMessages = ({ messages }) => (
  <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 overflow-hidden">
    <div className="flex items-center justify-between p-6 border-b border-gray-700/50">
      <h3 className="text-lg font-semibold text-white">Recent Messages</h3>
      <Link to="/admin/messages" className="text-blue-400 hover:text-blue-300 text-sm flex items-center gap-1">
        View all <ExternalLink className="w-4 h-4" />
      </Link>
    </div>
    <div className="divide-y divide-gray-700/50">
      {messages.length === 0 ? (
        <div className="p-6 text-center text-gray-400">No messages yet</div>
      ) : (
        messages.slice(0, 5).map((msg, i) => (
          <div key={i} className="p-4 hover:bg-gray-700/30 transition-colors">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                {msg.name?.charAt(0).toUpperCase() || '?'}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-medium text-white truncate">{msg.name}</p>
                  <span className="text-xs text-gray-400 flex-shrink-0">
                    {new Date(msg.createdAt).toLocaleDateString()}
                  </span>
                </div>
                <p className="text-sm text-gray-400 truncate">{msg.email}</p>
                <p className="text-sm text-gray-300 mt-1 line-clamp-2">{msg.message}</p>
              </div>
              {!msg.read && (
                <span className="w-2 h-2 bg-blue-500 rounded-full flex-shrink-0"></span>
              )}
            </div>
          </div>
        ))
      )}
    </div>
  </div>
);

const QuickActions = () => {
  const actions = [
    { label: 'Add Project', link: '/admin/projects', color: 'from-blue-500 to-blue-600' },
    { label: 'Edit Hero', link: '/admin/hero', color: 'from-purple-500 to-purple-600' },
    { label: 'Update About', link: '/admin/about', color: 'from-green-500 to-green-600' },
    { label: 'View Messages', link: '/admin/messages', color: 'from-orange-500 to-orange-600' },
  ];

  return (
    <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
      <h3 className="text-lg font-semibold text-white mb-4">Quick Actions</h3>
      <div className="grid grid-cols-2 gap-3">
        {actions.map((action, i) => (
          <Link
            key={i}
            to={action.link}
            className={`bg-gradient-to-r ${action.color} text-white text-center py-3 px-4 rounded-xl font-medium hover:opacity-90 transition-opacity`}
          >
            {action.label}
          </Link>
        ))}
      </div>
    </div>
  );
};

const Dashboard = () => {
  const { apiCall } = useApi();
  const [stats, setStats] = useState({
    projects: 0,
    messages: { total: 0, unread: 0 },
    recentMessages: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const [projectsRes, messagesRes] = await Promise.all([
        apiCall('/projects'),
        apiCall('/messages')
      ]);

      setStats({
        projects: projectsRes.length,
        messages: messagesRes.counts,
        recentMessages: messagesRes.messages
      });
    } catch (error) {
      console.error('Dashboard fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white">Dashboard</h1>
        <p className="text-gray-400 mt-1">Welcome back! Here's your portfolio overview.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Projects"
          value={stats.projects}
          icon={FolderKanban}
          color="bg-gradient-to-br from-blue-500 to-blue-600"
          link="/admin/projects"
        />
        <StatCard
          title="Total Messages"
          value={stats.messages.total}
          icon={Mail}
          color="bg-gradient-to-br from-purple-500 to-purple-600"
          link="/admin/messages"
        />
        <StatCard
          title="Unread Messages"
          value={stats.messages.unread}
          icon={Mail}
          color="bg-gradient-to-br from-orange-500 to-orange-600"
          link="/admin/messages"
        />
        <StatCard
          title="Starred Messages"
          value={stats.messages.starred || 0}
          icon={Users}
          color="bg-gradient-to-br from-green-500 to-green-600"
          link="/admin/messages"
        />
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <RecentMessages messages={stats.recentMessages} />
        </div>
        <div>
          <QuickActions />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
